type NumericValue = number | bigint | string;

type TOptions = {
  currency: string;
  minimumFractionDigits: number;
  maximumFractionDigits?: number;
};

const parseNumericValue = (value: NumericValue): number | bigint => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && isNaN(parsed) ? 0 : parsed;
};

const cache = new Map<string, Intl.NumberFormat>();

const getKey = ({ currency, minimumFractionDigits }: TOptions): string =>
  `${currency}:${minimumFractionDigits}`;

const obtainFormatterCurrency = (options: TOptions): Intl.NumberFormat => {
  const key = getKey(options);
  const formatter = cache.get(key);

  if (formatter) {
    return formatter;
  }

  const formatterNew = new Intl.NumberFormat('en-US', {
    style: 'currency',
    ...options,
  });
  cache.set(key, formatterNew);

  return formatterNew;
};

/**
 * Formats a numeric value as a currency string using `Intl.NumberFormat`.
 * Formatter instances are cached per `currency` + `minimumFractionDigits` pair.
 *
 * @example
 * formatCurrency(1234.5)           // → "$1,235"
 * formatCurrency(1234.5, 'EUR', 2) // → "€1,234.50"
 * formatCurrency('99.9', 'GBP', 2) // → "£99.90"
 */
export const formatCurrency = (
  value: NumericValue,
  currency = 'USD',
  minimumFractionDigits = 0,
  maximumFractionDigits?: number,
): string =>
  obtainFormatterCurrency({
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parseNumericValue(value));
